import { useEffect, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Content, InfoCard, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { Grid, Typography } from '@material-ui/core';

interface BuildInfoResponse {
  nodeVersion: string;
  appVersion: string;
  gitSha?: string;
}

export const BuildInfo = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [data, setData] = useState<BuildInfoResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchBuildInfo = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('app-info');
        const response = await fetchApi.fetch(`${baseUrl}/build-info`);
        if (!response.ok) {
          throw new Error(`Failed to fetch build info: ${response.status}`);
        }
        const json = await response.json();
        if (!cancelled) {
          setData(json);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchBuildInfo();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (!data) {
    return null;
  }

  return (
    <Content>
      <Grid container spacing={3} direction="column">
        <Grid item>
          <InfoCard title="Build Information">
            <Typography variant="body1" paragraph>
              <strong>Node version:</strong> {data.nodeVersion}
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>App version:</strong> {data.appVersion}
            </Typography>
            {data.gitSha && (
              <Typography variant="body1" paragraph>
                <strong>Commit:</strong> <code style={{ fontSize: '0.9em' }}>{data.gitSha}</code>
              </Typography>
            )}
          </InfoCard>
        </Grid>
      </Grid>
    </Content>
  );
};
